/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {Group, Link} from '@ion/core/src/types/entities';
import Dropdown, {createOptions, Options} from '@ion/dls/src/components/menus/Dropdown';

/**
 * Defines the prop required by this component.
 */
export type AdminGroupChooserProps = {
  onChange?: (group: Group | Group[], multiSelect: boolean, isNewGroup: boolean) => void;
  groupOptions: Group[];
  loading: boolean;
  multiSelect?: boolean;
  readOnly?: boolean;
  required?: boolean;
  includeNone?: boolean
  hasError?: boolean;
  errorText?: string;
  classes?: string;
  defaultValue?: Link;
  fieldLabel?: string;
  placeholder?: string;
  noneOptionValue?: any;
  noneOptionLabel?: string;
  isAdd?: boolean;
}

const useStyles = makeStyles(theme => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  }
}));

/**
 * The AdminGroupChooser provides a list of groups for display within a
 * dropdown list allowing the user to select a group from the list.
 *
 * ```typescript
 *
 *     import {AdminGroupChooser} from '@ion/features/src/entities/group/AdminGroupChooser';
 *
 *     <AdminGroupChooser defaultValue={group}
 *        groupOptions={groupOptions}
 *        hasError=""
 *        onChange={selectedGroupCallback} />
 *
 * ```
 *
 * @onChange     Callback prop which will be invoked with the selected Group.
 * @readOnly     When true, the component will be displayed in read-only mode;
 *               that is, disabled. Defaults to false.
 * @required     When true, the required indicator will be displayed.
 * @hasError     When true, the hasError indicator will be displayed.
 * @classes      Optional classes to apply to the wrapping element.
 * @includeNone  When true (default), a localized 'None' option will be displayed.
 * @defaultValue Default value of the field
 */
export const AdminGroupChooser = ({
  groupOptions,
  loading,
  onChange,
  classes,
  defaultValue,
  errorText = '',
  noneOptionValue,
  noneOptionLabel,
  fieldLabel = '',
  placeholder = '',
  isAdd = false,
  hasError = false,
  required = false,
  readOnly = false,
  includeNone = false,
  multiSelect = false
}: AdminGroupChooserProps) => {
  const _classes                = useStyles();
  const [groups, setGroups]     = useState<Group[]>(null);
  const [options, setOptions]   = useState<Options[]>([]);
  const [selected, setSelected] = useState<number[] | string[] | number | string>(multiSelect ? [] : null);

  useEffect(() => {
    setSelected(multiSelect ? [] : null);
    setGroups(groupOptions);
    setOptions(createOptions({
      items     : groupOptions,
      valProp   : 'id',
      labelProp : 'name'
    }));
  }, [groupOptions, multiSelect]);

  useEffect(() => {
    const target = 'removeAllGroup';
    const shouldDisable = (
      multiSelect &&
      Array.isArray(selected) &&
      selected?.some(({value}) => value === target)
    );
    setOptions(prevOptions => prevOptions.map(option => ({
      ...option,
      disabled : shouldDisable ? option.value !== target : false
    })));
  }, [multiSelect, selected]);

  // if a default group is provided set it as the selected ...
  useEffect(() => {
    if (defaultValue && !selected) {
      setSelected(defaultValue.title);
    }
  }, [defaultValue]);

  const getIds = (selectedKey) => {
    return selectedKey?.map(({value}) => value) || [];
  };

  /**
   * Provides a utility which filters a list of groups for the
   * currently selected id.
   */
  const getSelectedValue = (groups: Group[], selectedKey: any) => {
    if (multiSelect) {
      const idValues = getIds(selectedKey);
      const selectedGroups = groups?.filter((item) => {
        return idValues.includes(item?.id) || selectedKey.includes(item.id);
      });
      return selectedGroups;
    }
    return groups.find(({name}: Group) => name === selectedKey);
  };

  return (
    <div className={classes || _classes.root}>
      <Dropdown displaySelectedLabel
        multiSelect={multiSelect}
        required={required}
        disabled={readOnly || loading}
        hasError={hasError}
        errorText={errorText}
        value={!loading ? selected : multiSelect ? [translate('common:states.loading')] : translate('common:states.loading')}
        options={options}
        includeNone={includeNone}
        noneOptionValue={noneOptionValue}
        noneOptionLabel={noneOptionLabel}
        fieldLabel={fieldLabel || translate('common:features.groups.label')}
        placeholder={placeholder || translate('common:features.groups.placeholder')}
        preferPlaceholder={!loading && !selected}
        onChange={onChange ? function({target}) {
          const ids = target.value as unknown as number;
          setSelected(ids);
          onChange(getSelectedValue(groups, ids), multiSelect, multiSelect ? getIds(ids).includes(isAdd ? 'newGroup' : 'removeAllGroup') : false);
        } : null} />
    </div>
  );
};

export default AdminGroupChooser;
